<template>
    <span class="agent-trust-score">
        <q-btn flat round dense class="agent-trust-score-btn" @click.stop="showPopup = true">
            <img :src="icon" alt="Agent Trust Score" class="agent-trust-score-icon"/>
            <q-tooltip anchor="top middle" self="bottom middle" :offset="[0, 6]" class="bg-grey-9" style="font-size: 10px;">Agent Trust Score</q-tooltip>
        </q-btn>

        <!-- AGENT TRUST SCORE POPUP -->
        <q-dialog v-model="showPopup">
            <q-card class="trust-score-card">
                <div class="flex full-width justify-between items-center no-wrap">
                    <div class="flex items-center no-wrap">
                        <img :src="icon" alt="" class="trust-score-title-icon"/>
                        <div class="trust-score-title">Agent Trust Score</div>
                    </div>
                    <q-btn flat round dense icon="close" size="sm" color="grey-8" @click="showPopup = false"/>
                </div>

                <div v-if="error" class="trust-score-error flex items-center no-wrap">
                    <q-icon name="error_outline" size="18px" color="negative" class="q-mr-sm"/>
                    <div>{{ error }}</div>
                </div>

                <div v-else-if="!scores.length" class="trust-score-empty flex items-center no-wrap">
                    <q-icon name="info_outline" size="18px" color="grey-7" class="q-mr-sm"/>
                    <div>{{ emptyMessage }}</div>
                </div>

                <template v-else>
                    <div v-for="score in normalizedScores" :key="score.score_name" class="trust-score-metric">
                        <div class="trust-score-metric-body">
                            <div class="trust-score-metric-name">
                                {{ score.score_name }}
                                <q-icon v-if="score.justification" name="info_outline" size="14px" color="grey-7" class="trust-score-info-icon">
                                    <q-tooltip anchor="top middle" self="bottom middle" :offset="[0, 6]" max-width="320px" class="bg-grey-9" style="font-size: 11px; white-space: pre-line;">{{ score.justification }}</q-tooltip>
                                </q-icon>
                            </div>
                            <div v-if="!score.lowerIsBetter" class="trust-score-threshold-label" :style="{ left: score.thresholdPercent }">Threshold {{ score.threshold }}</div>

                            <div class="trust-score-track">
                                <div class="trust-score-fill" :class="{ 'trust-score-fill--below': !score.meetsThreshold }" :style="{ width: score.valuePercent }"></div>
                                <div class="trust-score-threshold-line" :style="{ left: score.thresholdPercent }"></div>
                                <div v-if="score.lowerIsBetter" class="trust-score-threshold-label trust-score-threshold-label--under-line" :style="{ left: score.thresholdPercent }">Threshold {{ score.threshold }}</div>
                            </div>

                            <div class="flex full-width justify-between trust-score-scale">
                                <span>0</span>
                                <span>1</span>
                            </div>
                        </div>
                        <div class="trust-score-value">{{ score.value.toFixed(2) }}</div>
                    </div>

                    <template v-if="explainedScores.length">
                        <div class="trust-score-divider"></div>
                        <div class="trust-score-section-title">What these scores mean</div>
                        <div v-for="score in explainedScores" :key="`meaning-${score.score_name}`" class="q-mt-sm">
                            <div class="trust-score-meaning-name">{{ score.score_name }}</div>
                            <div class="trust-score-meaning-text">{{ score.description }}</div>
                        </div>
                    </template>
                </template>
            </q-card>
        </q-dialog>
    </span>
</template>

<script>
import agentTrustScoreIcon from '@/assets/icons/agent-trust-score.svg';

const SCORE_DESCRIPTIONS = {
    'relevancy score': 'How well the response addresses your request; higher is better.',
    'groundedness score': 'How well the response is grounded in the provided sources; higher is better.',
    'hallucination score': 'Likelihood the response includes unsupported or invented claims; lower is better.',
};

// The critic agent does not send `lower_is_better`, so the scores it applies to are known by name.
const LOWER_IS_BETTER_SCORES = new Set(['hallucination score']);

const toPercent = (value) => `${Math.min(Math.max(value, 0), 1) * 100}%`;

export default {
    name: 'AgentTrustScoreButton',

    // Each score is { score_name, value, threshold, lower_is_better?, justification? }, with value and threshold on a 0-1 scale.
    props: {
        scores: {
            type: Array,
            default: () => [],
        },
        // Shown in place of the scores when they could not be generated.
        error: {
            type: String,
            default: '',
        },
        // Shown when there are no scores and no error.
        emptyMessage: {
            type: String,
            default: 'Agent trust scores are not available for this response.',
        },
    },

    data() {
        return {
            icon: agentTrustScoreIcon,
            showPopup: false,
        };
    },

    computed: {
        normalizedScores() {
            return this.scores.map((score) => {
                const value = Number(score.value) || 0;
                const threshold = Number(score.threshold) || 0;
                const name = String(score.score_name || '').trim().toLowerCase();
                const lowerIsBetter = Boolean(score.lower_is_better ?? LOWER_IS_BETTER_SCORES.has(name));
                return {
                    score_name: score.score_name,
                    value,
                    threshold,
                    valuePercent: toPercent(value),
                    thresholdPercent: toPercent(threshold),
                    lowerIsBetter,
                    meetsThreshold: lowerIsBetter ? value <= threshold : value >= threshold,
                    description: SCORE_DESCRIPTIONS[name] || '',
                    justification: String(score.justification || '').trim(),
                };
            });
        },

        explainedScores() {
            return this.normalizedScores.filter((score) => score.description);
        },
    },
};
</script>

<style scoped>
.agent-trust-score {
    display: inline-flex;
    flex-shrink: 0;
}

.agent-trust-score-btn {
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
    padding: 0;
}

.agent-trust-score-icon {
    width: 65%;
    height: 65%;
}

.trust-score-card {
    width: 440px;
    max-width: 90vw;
    padding: 16px 20px;
    border-radius: 10px;
}

.trust-score-title-icon {
    width: 24px;
    height: 24px;
    margin-right: 10px;
}

.trust-score-title {
    font-size: 16px;
    font-weight: 700;
    color: #242424;
}

.trust-score-metric {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 56px;
    column-gap: 16px;
    margin-top: 22px;
}

.trust-score-metric-body {
    position: relative;
    min-width: 0;
}

.trust-score-metric-name {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    color: #242424;
}

.trust-score-info-icon {
    margin-left: 4px;
    cursor: pointer;
}

.trust-score-error {
    margin-top: 18px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #fdecea;
    color: #a12a26;
    font-size: 12px;
}

.trust-score-empty {
    margin-top: 18px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #f4f4f4;
    color: #5f5f5f;
    font-size: 12px;
}

.trust-score-threshold-label {
    position: absolute;
    top: 1px;
    transform: translateX(-50%);
    color: #2568c1;
    font-size: 10px;
    white-space: nowrap;
}

.trust-score-threshold-label--under-line {
    top: 20px;
    line-height: 12px;
}

.trust-score-track {
    position: relative;
    height: 8px;
    margin-top: 12px;
    border-radius: 4px;
    background: #e8e8e8;
}

.trust-score-fill {
    height: 100%;
    border-radius: 4px;
    background: #2e9d57;
}

.trust-score-fill--below {
    background: #d9534f;
}

.trust-score-threshold-line {
    position: absolute;
    top: -8px;
    height: 26px;
    border-left: 2px dashed #2568c1;
    transform: translateX(-1px);
}

.trust-score-scale {
    margin-top: 6px;
    color: #8d8d8d;
    font-size: 10px;
}

.trust-score-value {
    color: #2568c1;
    font-size: 20px;
    font-weight: 700;
    line-height: 1;
    text-align: right;
}

.trust-score-divider {
    width: 100%;
    margin: 16px 0 12px;
    border-top: 1px solid #e6e6e6;
}

.trust-score-section-title {
    font-size: 13px;
    font-weight: 700;
    color: #242424;
}

.trust-score-meaning-name {
    font-size: 11px;
    font-weight: 600;
    color: #242424;
}

.trust-score-meaning-text {
    margin-top: 2px;
    font-size: 11px;
    color: #8d8d8d;
}
</style>
